import PropTypes from 'prop-types';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { ChatMessage } from './ChatMessage';
import { ContactCardBubble } from './ContactCardBubble';

// Slow enough to be watched, fast enough not to hold up the composer unlocking
// behind it. The card is the OUTGOING half's replacement, so it leads by a beat
// — the speech fades first, then the card resolves into the space it left.
const SPEECH_FADE_S = 0.18;
const CARD_FADE_S = 0.24;
const CARD_DELAY_S = 0.08;

// The container's size change is the morph. A spring rather than a duration:
// the card is taller than the one-line speech bubble, and easing that height
// reads as a panel opening where a settle reads as one thing becoming another.
const MORPH_LAYOUT = { type: 'spring', damping: 26, stiffness: 320 };

// Drives ContactCardBubble's ICON_POP_VARIANTS, which declare `variants` and no
// animate of their own precisely so a parent can own the beat. Inheriting them
// here is why the person icon pops as the card lands instead of arriving with it.
const CARD_VARIANTS = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

/**
 * AnonNameCardMorph — the Anon's name bubble becoming their contact card
 * (user design 2026-08-20).
 *
 * THE MORPH IS THE CONFIRMATION. It replaces ThreadConnectedCard, the frosted
 * "Connected with Mark" panel that took the middle of the screen for 1.8s and
 * faded. Two reasons that lost:
 *
 *   - It fired on the ANON'S share completing, so it claimed a connection even
 *     when the host had shared nothing back. A card that can be wrong about the
 *     one fact it states is worse than no card.
 *   - It confirmed OVER the thread. This confirms IN it, by changing the thing
 *     the person just made — their name bubble — into the durable artifact the
 *     host's card already is. The receipt and the record become one object,
 *     which is also what makes it tappable afterwards.
 *
 * BOTH SIDES RENDER IT (corrected 2026-08-20, same day). The first pass gated
 * this to the Anon's own view on the theory that the host should keep the
 * bubble's "verbatim greeting" — but under THREAD there is no greeting to keep.
 * The greeting-shape composer (ChatInputMorphing, extractName: true) is gated
 * `!isThreadVariant()`; THREAD's only name entry is a titled Name field
 * submitted with extraction OFF, so `content` and `anonName` are the same
 * string and the "preserved" bubble said exactly what the card says. Both sides
 * are prompted for name and contact, so both shares land as cards.
 *
 * The caller decides by comparing the two: the bubble carries something the
 * card cannot only when the typed words differ from the stored name — a
 * CHAT-variant greeting, where "Hi it's Franklin" must not flatten to a card
 * reading the same. See ConnectionPageShared's `bubbleIsJustTheName`.
 *
 * Both halves render at once, stacked, with whichever is inactive pulled out of
 * flow. No AnimatePresence: the outgoing half must keep occupying the row while
 * the incoming one grows into it, and an exit animation that unmounts collapses
 * the row for a frame. The `layout` wrapper animates the height difference, and
 * that height change IS the morph — the crossfade only hides the seam.
 *
 * MOUNT IS SILENT. `initial={false}` on both halves means a returner opening a
 * finished thread gets the card already there, statically. Only a live flip
 * animates, which is the same rule the contact-card entrance uses (pops on
 * land, never on chat reopen).
 *
 * TWO TAP PROPS, not one, because the two states are different affordances and
 * the halves are not interchangeable. On the Anon's own row both go to the
 * share edit overlay; on the host's row the speech half has never been tappable
 * and stays that way, while the card opens the Anon's detail page.
 *
 * @param {string} text - the name bubble's verbatim content (their exact words)
 * @param {string} cardName - name on the card (the stored name, not the words)
 * @param {boolean} showCard - true once the contact value has committed
 * @param {boolean} isSent - own row (right, blue) vs the peer's (left, grey)
 * @param {function} onSpeechClick - tap on the pre-contact speech bubble
 * @param {function} onCardClick - tap on the card
 */
export const AnonNameCardMorph = ({
  text,
  cardName,
  showCard,
  isSent = true,
  onSpeechClick,
  onCardClick,
}) => {
  // SETTLED THREADS RENDER THE CARD ALONE. If the card was already showing at
  // mount, the flip has happened and can never happen again for this message —
  // so there is nothing to morph between. Keeping both halves painted under a
  // `layout` wrapper made framer re-measure this node on every render of the
  // message list, forever, for every finished thread. Captured at mount, not
  // derived per render: a live flip must keep the dual render for the whole
  // animation, and reading `showCard` directly would tear it down mid-flight.
  const [mountedSettled] = useState(() => showCard);

  if (mountedSettled) {
    return <ContactCardBubble name={cardName} isSent={isSent} onClick={onCardClick} />;
  }

  // Out of flow, invisible to taps, but still painted — see the no-AnimatePresence
  // note above.
  const parked = { position: 'absolute', inset: 0, pointerEvents: 'none' };

  return (
    <motion.div layout transition={MORPH_LAYOUT} style={{ position: 'relative', width: '100%' }}>
      <motion.div
        initial={false}
        variants={CARD_VARIANTS}
        animate={showCard ? 'visible' : 'hidden'}
        transition={{ duration: CARD_FADE_S, delay: showCard ? CARD_DELAY_S : 0 }}
        style={showCard ? undefined : parked}
      >
        <ContactCardBubble name={cardName} isSent={isSent} onClick={onCardClick} />
      </motion.div>

      <motion.div
        initial={false}
        animate={{ opacity: showCard ? 0 : 1 }}
        transition={{ duration: SPEECH_FADE_S }}
        style={showCard ? parked : undefined}
      >
        <div onClick={onSpeechClick} style={onSpeechClick ? { cursor: 'pointer' } : undefined}>
          <ChatMessage message={text} type={isSent ? 'sent' : 'received'} />
        </div>
      </motion.div>
    </motion.div>
  );
};

AnonNameCardMorph.propTypes = {
  text: PropTypes.string,
  cardName: PropTypes.string,
  showCard: PropTypes.bool,
  isSent: PropTypes.bool,
  onSpeechClick: PropTypes.func,
  onCardClick: PropTypes.func,
};

export default AnonNameCardMorph;
